/**
 * Design tokens — single source of truth.
 *
 * Tailwind config extends from this. When mobile ships, the same tokens drive
 * React Native StyleSheet objects.
 *
 * See spec section 5.2.
 */

export const tokens = {
  colors: {
    // Page surfaces
    paper: "#FAF6EE",
    paperWarm: "#F1EBDF",
    paperDeep: "#E5DDC9",
    // Text
    ink: "#1A1813",
    inkSoft: "#4A4640",
    // Darkened from #807A6F (3.95:1 on paper) to satisfy WCAG AA at body
    // sizes — used for small captions, legends, and metadata text.
    inkFaded: "#6A645A",
    // Accent — oxblood, the "serious music textbook" red
    accent: "#872932",
    accentSoft: "#B8454F",
    // Semantic
    success: "#0F6E56",
    warning: "#854F0B",
    danger: "#A32D2D",
    info: "#185FA5",
    // Per-track identity (CURRICULUM.md §1). pitch→info, harmony→accent,
    // modes→warning reuse existing hues; intervals and dictation get their own
    // so all five Learn lanes stay distinguishable (dictation was documented as
    // "accent", colliding with harmony — resolved to plum here).
    trackColor: {
      pitch: "#185FA5",
      intervals: "#147D74",
      harmony: "#872932",
      modes: "#854F0B",
      dictation: "#6D4C7D",
    },
    // Mastery tiers (curriculum tree)
    tier: {
      mastered: "#0F6E56",
      practicing: "#185FA5",
      introduced: "#854F0B",
      locked: "#6A645A",
    },
  },
  radius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
  },
  fonts: {
    serif: '"Cormorant Garamond", Georgia, serif',
    sans: '"DM Sans", -apple-system, sans-serif',
    mono: 'ui-monospace, "SF Mono", Menlo, monospace',
  },
  fontSize: {
    xs: "11px",
    sm: "13px",
    base: "16px",
    lg: "18px",
    xl: "22px",
    "2xl": "28px",
    "3xl": "36px",
    "4xl": "48px",
  },
  spacing: {
    1: "4px",
    2: "8px",
    3: "12px",
    4: "16px",
    5: "24px",
    6: "32px",
    7: "48px",
    8: "64px",
  },
} as const;

export type ColorToken = keyof typeof tokens.colors;
export type RadiusToken = keyof typeof tokens.radius;
